import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchDealers,
  createDealer,
  updateDealer,
  deleteDealer,
} from '../redux/slices/dealersSlice';
import { Modal, ConfirmDialog, LoadingSpinner, EmptyState } from '../components/Common';
import { useSearch } from '../context/SearchContext';
import { useEntityDetails } from '../context/EntityDetailsContext';
import { useLanguage } from '../context/LanguageContext';
import { printTable } from '../utils/printTable';

const emptyForm = {
  name: '',
  nickname: '',
  contact_person: '',
  email: '',
  tel: '',
  street: '',
  zip: '',
  city: '',
  templateID: '',
};

const Dealers = () => {
  const dispatch = useDispatch();
  const { searchTerm } = useSearch();
  const { openDealer } = useEntityDetails();
  const { t } = useLanguage();
  const { items: dealers, loading, error } = useSelector((state) => state.dealers);

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => {
    dispatch(fetchDealers());
  }, [dispatch]);

  const filtered = dealers.filter((d) => {
    const term = searchTerm.toLowerCase();
    return (
      d.name?.toLowerCase().includes(term) ||
      d.contact_person?.toLowerCase().includes(term) ||
      d.city?.toLowerCase().includes(term) ||
      d.tel?.toLowerCase().includes(term)
    );
  });

  const openCreate = () => {
    setEditingId(null);
    setFormData(emptyForm);
    setShowModal(true);
  };

  const openEdit = (dealer) => {
    setEditingId(dealer.id);
    setFormData({
      name: dealer.name || '',
      nickname: dealer.nickname || '',
      contact_person: dealer.contact_person || '',
      email: dealer.email || '',
      tel: dealer.tel || '',
      street: dealer.street || '',
      zip: dealer.zip || '',
      city: dealer.city || '',
      templateID: dealer.templateID ?? '',
    });
    setShowModal(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = { ...formData, templateID: formData.templateID === '' ? null : Number(formData.templateID) };
    const action = editingId ? updateDealer({ id: editingId, data: payload }) : createDealer(payload);
    dispatch(action)
      .unwrap()
      .then(() => setShowModal(false))
      .catch((err) => alert(err?.error || t('dealers.saveFailed')));
  };

  const handleDelete = () => {
    dispatch(deleteDealer(deleteTarget.id))
      .unwrap()
      .then(() => setDeleteTarget(null))
      .catch((err) => console.error('Failed to delete dealer:', err));
  };

  if (error) {
    return (
      <div className='card bg-red-50 border border-red-200'>
        <p className='text-red-600'>{t('dealers.errorLoading', { error })}</p>
      </div>
    );
  }

  const handlePrint = () => {
    printTable({
      title: t('dealers.title'),
      columns: [
        { header: t('common.name') },
        { header: t('dealers.templateId') },
        { header: t('dealers.contact') },
        { header: t('dealers.phone') },
        { header: t('dealers.city') },
      ],
      rows: filtered.map((d) => [d.name, d.templateID, d.contact_person, d.tel, d.city]),
    });
  };

  return (
    <div>
      <div className='flex justify-between items-center mb-6'>
        <div>
          <h1 className='text-3xl font-bold text-gray-900'>{t('dealers.title')}</h1>
          <p className='text-gray-600 mt-1'>{t('dealers.subtitle')}</p>
        </div>
        <div className='flex items-center gap-3'>
          <button onClick={handlePrint} className='btn-secondary'>
            🖨️ {t('common.print')}
          </button>
          <button onClick={openCreate} className='btn-primary'>
            {t('dealers.addDealer')}
          </button>
        </div>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon='🏬'
          title={t('dealers.noDealersTitle')}
          message={t('dealers.noDealersMessage')}
          actionLabel={t('dealers.addDealer')}
          onAction={openCreate}
          hasItems={dealers.length > 0}
        />
      ) : (
        <div className='card overflow-x-auto'>
          <table className='min-w-full divide-y divide-gray-200'>
            <thead className='bg-gray-50'>
              <tr>
                <th className='px-4 py-2 text-left text-xs font-medium text-gray-500'>{t('common.name')}</th>
                <th className='px-4 py-2 text-left text-xs font-medium text-gray-500'>{t('dealers.templateId')}</th>
                <th className='px-4 py-2 text-left text-xs font-medium text-gray-500'>{t('dealers.contact')}</th>
                <th className='px-4 py-2 text-left text-xs font-medium text-gray-500'>{t('dealers.phone')}</th>
                <th className='px-4 py-2 text-left text-xs font-medium text-gray-500'>{t('dealers.city')}</th>
                <th className='px-4 py-2 text-right text-xs font-medium text-gray-500'>{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody className='bg-white divide-y divide-gray-200'>
              {filtered.map((d) => (
                <tr key={d.id} className='hover:bg-gray-50'>
                  <td
                    className='px-4 py-3 text-sm font-medium text-blue-600 hover:text-blue-800 cursor-pointer'
                    onClick={() => openDealer(d.id, d)}
                  >
                    {d.name}
                  </td>
                  <td className='px-4 py-3 text-sm text-gray-900'>{d.templateID ?? '-'}</td>
                  <td className='px-4 py-3 text-sm text-gray-900'>{d.contact_person || '-'}</td>
                  <td className='px-4 py-3 text-sm text-gray-900'>{d.tel || '-'}</td>
                  <td className='px-4 py-3 text-sm text-gray-900'>{d.city || '-'}</td>
                  <td className='px-4 py-3 text-right space-x-3'>
                    <button onClick={() => openDealer(d.id, d)} className='text-sm text-blue-600 hover:text-blue-800'>
                      {t('common.view')}
                    </button>
                    <button onClick={() => openEdit(d)} className='text-sm text-blue-600 hover:text-blue-800'>
                      {t('common.edit')}
                    </button>
                    <button
                      onClick={() => setDeleteTarget(d)}
                      className='text-sm text-red-600 hover:text-red-800'
                    >
                      {t('common.delete')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editingId ? t('dealers.editDealer') : t('dealers.createDealer')}
        size='md'
      >
        <form onSubmit={handleSubmit}>
          <div className='space-y-4'>
            <div className='grid grid-cols-3 gap-4'>
              <div className='col-span-2'>
                <label className='block text-sm font-medium text-gray-700 mb-2'>{t('common.name')} *</label>
                <input
                  type='text'
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className='input-field'
                  required
                />
              </div>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>{t('dealers.templateId')}</label>
                <input
                  type='number'
                  value={formData.templateID}
                  onChange={(e) => setFormData({ ...formData, templateID: e.target.value })}
                  className='input-field'
                />
              </div>
            </div>
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-2'>{t('dealers.nickname')}</label>
              <input
                type='text'
                value={formData.nickname}
                onChange={(e) => setFormData({ ...formData, nickname: e.target.value })}
                className='input-field'
              />
            </div>
            <div className='grid grid-cols-2 gap-4'>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>{t('dealers.contactPerson')}</label>
                <input
                  type='text'
                  value={formData.contact_person}
                  onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                  className='input-field'
                />
              </div>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>{t('dealers.phone')}</label>
                <input
                  type='text'
                  value={formData.tel}
                  onChange={(e) => setFormData({ ...formData, tel: e.target.value })}
                  className='input-field'
                />
              </div>
            </div>
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-2'>{t('dealers.email')}</label>
              <input
                type='email'
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className='input-field'
              />
            </div>
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-2'>{t('dealers.street')}</label>
              <input
                type='text'
                value={formData.street}
                onChange={(e) => setFormData({ ...formData, street: e.target.value })}
                className='input-field'
              />
            </div>
            <div className='grid grid-cols-3 gap-4'>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>{t('dealers.zip')}</label>
                <input
                  type='text'
                  value={formData.zip}
                  onChange={(e) => setFormData({ ...formData, zip: e.target.value })}
                  className='input-field'
                />
              </div>
              <div className='col-span-2'>
                <label className='block text-sm font-medium text-gray-700 mb-2'>{t('dealers.city')}</label>
                <input
                  type='text'
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  className='input-field'
                />
              </div>
            </div>
            <div className='flex justify-end space-x-3 pt-4'>
              <button type='button' onClick={() => setShowModal(false)} className='btn-secondary'>
                {t('common.cancel')}
              </button>
              <button type='submit' className='btn-primary'>
                {editingId ? t('dealers.saveChanges') : t('dealers.createDealer')}
              </button>
            </div>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title={t('dealers.deleteDealer')}
        message={t('dealers.confirmDelete', { name: deleteTarget?.name })}
      />
    </div>
  );
};

export default Dealers;
